import React from 'react';
import type { SortValue } from '@engage/filters';
import { useReduxState } from '@engage/redux';
import { getCurrentFiltersFromSearch } from '@members/pg-library/Filters/redux';

export type Option = {
  label: string;
  content: React.ReactElement<any>;
  value: string | SortValue;
  selected?: boolean;
  group?: string;
};

const selectedOptionNames = (options: Option[], filterPositioning: string[]): Option[] =>
  filterPositioning.reduce((acc, filter) => {
    const option = options.find(o => o.value.toString() === filter);

    return acc.concat(option ? option : []);
  }, [] as Option[]);

export const useMultiSelectLabel = (
  ref: React.RefObject<HTMLDivElement>,
  parentRef: React.RefObject<HTMLDivElement>,
  options: Option[],
  name?: string,
) => {
  const currentURLFilters = useReduxState(getCurrentFiltersFromSearch);
  const currentURLFilter =
    currentURLFilters.find(a => !!name && a.name === name)?.value ?? [];
  const orderedOptionNames = selectedOptionNames(options, currentURLFilter);

  const [additionalLabel, setAdditionalLabel] = React.useState(0);
  const [prevSelectionList, setSelectionList] = React.useState<string[]>([]);
  const CHEVRON_WIDTH = 24;

  React.useEffect(() => {
    // run this block whenever the length changes
    if (
      orderedOptionNames.length != prevSelectionList.length &&
      prevSelectionList.length > 0
    ) {
      setAdditionalLabel(0);
      setSelectionList(orderedOptionNames.map(x => x.value.toString()));
    }
    // check if the list of labels can fit inside the parent div.
    else if (
      ref &&
      parentRef &&
      (ref?.current?.clientWidth ?? 0) + CHEVRON_WIDTH >
        (parentRef?.current?.clientWidth ?? 0)
    ) {
      const newAdditionalLabel = additionalLabel + 1;
      if (newAdditionalLabel <= orderedOptionNames.length) {
        setAdditionalLabel(newAdditionalLabel);
      }
    }
  }, [ref?.current?.clientWidth, orderedOptionNames.length, prevSelectionList]);

  React.useEffect(() => {
    // find the newest addition to the list
    const newestSelection = orderedOptionNames?.find(
      c => !prevSelectionList.includes(c?.value.toString()),
    );
    if (
      newestSelection &&
      !prevSelectionList.includes(newestSelection.value.toString())
    ) {
      setSelectionList(prevSelectionList.concat(newestSelection.value.toString()));
    }
  }, [orderedOptionNames.length]);

  return { additionalLabel, orderedOptionNames };
};

export const useStickyFilter = (title: string) => {
  const [isStickyApplied, setIsStickyApplied] = React.useState(false);

  /* This intersection observer is used to determinen when position sticky
  is actually applied to a header. It does this by checking when the header
  intersects the 1px div (nav-container-top) above it. */
  const observer = new IntersectionObserver(
    function (entries) {
      if (entries[0].intersectionRatio === 0) {
        setIsStickyApplied(true);
      } else if (entries[0].intersectionRatio === 1) {
        setIsStickyApplied(false);
      }
    },
    { threshold: [0, 1] },
  );

  React.useEffect(() => {
    if (document.querySelector(`#nav-container-top-${title}`)) {
      observer.observe(document.querySelector(`#nav-container-top-${title}`) as Element);
    }
  }, [document.querySelector(`#nav-container-top-${title}`)]);

  return { isStickyApplied };
};
